import React, { useRef, useState, useEffect } from 'react'
import { useMobile } from 'src/hooks/useMobile'
import KitlookPlusIcon from 'src/components/Icons/KitlookPlusIcon'

import { useKitlook } from '../context/KitlookContext'
import CarouselNavButtons from './carousel/CarouselNavButtons'
import './customSlider.css'
import { KitlookProductCard } from './carousel/KitlookProductCard'

const itemSizeAndQttyToScroll = (screenWidth: number) => {
  if (screenWidth > 1024) return { width: 324, qtty: 4 }
  if (screenWidth > 768) return { width: 266, qtty: 3 }

  return { width: 166, qtty: 2 }
}

export function KitlookCarousel() {
  const { currentKit, hasNext, nextKit, hasPrev, prevKit } = useKitlook()
  const { isMobile, screenWidth } = useMobile()
  const listRef = useRef<HTMLUListElement | null>(null)

  const [currentPage, setCurrentPage] = useState<number>(0)
  const [navState, setNavState] = useState({ prev: false, next: true })
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)
  const [showDescriptionToggle, setShowDescriptionToggle] = useState(false)
  const descriptionRef = useRef<HTMLDivElement | null>(null)
  const isDescriptionExpandedRef = useRef(false)

  isDescriptionExpandedRef.current = isDescriptionExpanded

  const onScroll = () => {
    if (!listRef?.current) return
    const { scrollLeft, scrollWidth, offsetWidth } = listRef.current
    const itemWidth = itemSizeAndQttyToScroll(screenWidth).width

    setNavState({
      prev: scrollLeft > 10,
      next: Math.ceil(scrollLeft + offsetWidth) < scrollWidth,
    })

    setCurrentPage(Math.round(scrollLeft / itemWidth))
  }

  const moveTo = (index: number) => {
    if (!listRef?.current) return
    const itemWidth = itemSizeAndQttyToScroll(screenWidth).width

    listRef.current.scrollLeft = index * itemWidth
  }

  const onPrevClick = () => {
    const { qtty } = itemSizeAndQttyToScroll(screenWidth)

    moveTo(currentPage - qtty)
  }

  const onNextClick = () => {
    const { qtty } = itemSizeAndQttyToScroll(screenWidth)

    moveTo(currentPage + qtty)
  }

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollLeft = 0
    }

    setIsDescriptionExpanded(false)
  }, [currentKit])

  useEffect(() => {
    const element = descriptionRef.current

    if (!element) {
      return
    }

    const updateToggleVisibility = () => {
      if (isDescriptionExpandedRef.current) {
        return
      }

      setShowDescriptionToggle(element.scrollHeight > element.clientHeight + 1)
    }

    updateToggleVisibility()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateToggleVisibility)

      return () => window.removeEventListener('resize', updateToggleVisibility)
    }

    const resizeObserver = new ResizeObserver(updateToggleVisibility)

    resizeObserver.observe(element)

    return () => resizeObserver.disconnect()
  }, [currentKit?.description])

  if (!currentKit) return null

  return (
    <div className="w-full min-w-0 pt-7 py-[25px] px-0 md:px-9 md:py-7">
      <div className="hidden md:block mb-8">
        <div className="flex flex-wrap mb-2 gap-4">
          <p className="max-w-[550px] text-restructure-primary font-medium text-xl">
            {`${currentKit.name} (${currentKit.products.length} ${
              currentKit.products.length === 1 ? 'item' : 'itens'
            })`}
          </p>

          {hasPrev && (
            <button
              onClick={prevKit}
              type="button"
              className="cursor-pointer bg-transparent border-none p-0 font-bold text-primaryBlue hover:underline text-sm"
            >
              Ver Kit anterior
            </button>
          )}

          {hasNext && (
            <button
              onClick={nextKit}
              type="button"
              className="cursor-pointer bg-transparent border-none p-0 font-bold text-primaryBlue hover:underline text-sm"
            >
              Ver próximo Kit
            </button>
          )}
        </div>
        <div className="flex items-center gap-11 justify-between">
          <div className="relative max-w-[584px] min-w-0">
            <div
              className={
                isDescriptionExpanded
                  ? 'absolute top-0 left-0 z-10 w-full'
                  : undefined
              }
            >
              <div className={isDescriptionExpanded ? 'bg-white' : undefined}>
                <div
                  ref={descriptionRef}
                  className={`text-restructure-primary font-medium text-sm ${
                    isDescriptionExpanded ? '' : 'line-clamp-2'
                  }`}
                  dangerouslySetInnerHTML={{ __html: currentKit.description }}
                />
                {showDescriptionToggle && (
                  <button
                    type="button"
                    aria-expanded={isDescriptionExpanded}
                    onClick={() => setIsDescriptionExpanded((prev) => !prev)}
                    className="mt-1 cursor-pointer bg-transparent border-none p-0 font-bold text-primaryBlue hover:underline text-sm"
                  >
                    {isDescriptionExpanded ? 'Ver menos' : 'Ver mais'}
                  </button>
                )}
              </div>
              {isDescriptionExpanded && (
                <div
                  aria-hidden
                  className="h-10 bg-gradient-to-b from-white to-transparent pointer-events-none"
                />
              )}
            </div>
            {isDescriptionExpanded && (
              <div aria-hidden className="invisible pointer-events-none">
                <div
                  className="text-restructure-primary font-medium text-sm line-clamp-2"
                  dangerouslySetInnerHTML={{
                    __html: currentKit.description,
                  }}
                />
                {showDescriptionToggle && (
                  <span className="mt-1 block font-bold text-sm">Ver mais</span>
                )}
              </div>
            )}
          </div>

          {!isMobile && currentKit.products.length > 3 && (
            <CarouselNavButtons
              navState={navState}
              onNextClick={onNextClick}
              onPrevClick={onPrevClick}
            />
          )}
        </div>
      </div>

      <div className="kitlook-carousel relative w-full overflow-visible md:max-w-full md:overflow-hidden">
        <div className="kitlook-carousel overflow-visible md:overflow-hidden">
          <ul
            ref={listRef}
            className="kitlook-carouselList w-full min-w-0 max-w-[950px] flex flex-nowrap gap-3 md:gap-0 overflow-x-auto md:pb-10 scrollbar-hide scroll-smooth pr-4 md:pr-0"
            style={{ scrollSnapType: 'x mandatory' }}
            onScroll={onScroll}
          >
            {currentKit.products.map((product, i) => (
              <React.Fragment key={`kit-product-${product.productId}-${i}`}>
                <li
                  className="w-full self-stretch max-w-[137px] md:max-w-[217px] flex-shrink-0 scroll-snap-align-start"
                  style={{ scrollSnapAlign: 'start' }}
                >
                  <KitlookProductCard product={product} />
                </li>
                {i + 1 < currentKit.products.length && (
                  <div className="hidden md:block self-center -mt-[55px]">
                    <KitlookPlusIcon />
                  </div>
                )}
              </React.Fragment>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
